import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { BACKEND_URL } from '../api/axios';
import Navbar from './Navbar';
import Footer from './Footer';
import ResumeBuilder from './ResumeBuilder';
import { Link } from "react-router-dom";
import Autocomplete from './AutoComplete';
import {
  SKILLS_LIST, COLLEGES_LIST, LANGUAGES_LIST, DEGREES_LIST,
  JOB_TITLES_LIST, COMPANIES_LIST, CERTIFICATIONS_LIST, ISSUERS_LIST,
  CITIES_LIST, STATES_LIST, BOARDS_UNIVERSITIES_LIST,
} from '../data/Autocompletedata';

// Maps each master_items "type" to the static fallback list it should merge with.
const MASTER_TYPE_TO_STATIC_LIST = {
  skill: SKILLS_LIST,
  education: DEGREES_LIST,
  institution: COLLEGES_LIST,
  board_university: BOARDS_UNIVERSITIES_LIST,
  job_title: JOB_TITLES_LIST,
  company: COMPANIES_LIST,
  certification: CERTIFICATIONS_LIST,
  issued_by: ISSUERS_LIST,
  language: LANGUAGES_LIST,
};

// Minimal inline icon set for the profile-strength category grid — no external icon
// dependency, just enough detail per glyph to stay legible at 16px.
const CATEGORY_ICON_PATHS = {
  personal: <><circle cx="12" cy="8" r="3.4" /><path d="M5 20c0-3.6 3.1-6.2 7-6.2s7 2.6 7 6.2" /></>,
  documents: <><path d="M7 3h6l4 4v14H7z" /><path d="M13 3v4h4" /><path d="M9.5 12h5M9.5 15h5" /></>,
  verification: <><path d="M12 3l6.5 2.6v5.2c0 4.4-2.8 7.6-6.5 9-3.7-1.4-6.5-4.6-6.5-9V5.6z" /><path d="M9.2 12l1.9 1.9 3.7-3.9" /></>,
  skills: <path d="M12 2.5l2.4 5.2 5.6.6-4.2 3.8 1.2 5.6L12 14.9l-5 2.8 1.2-5.6-4.2-3.8 5.6-.6z" />,
  locations: <><path d="M12 21s-7-6.2-7-11a7 7 0 0114 0c0 4.8-7 11-7 11z" /><circle cx="12" cy="10" r="2.4" /></>,
  education: <><path d="M4 5.2c2.3-1 5.4-1 7.5 0v13.6c-2.1-1-5.2-1-7.5 0z" /><path d="M20 5.2c-2.3-1-5.4-1-7.5 0v13.6c2.1-1 5.2-1 7.5 0z" /></>,
  experience: <><rect x="3" y="8" width="18" height="11" rx="2" /><path d="M8.2 8V6.2A2.2 2.2 0 0110.4 4h3.2a2.2 2.2 0 012.2 2.2V8" /></>,
  certifications: <><circle cx="12" cy="8.5" r="4.8" /><path d="M8.3 12.6L6.5 21l5.5-3.2 5.5 3.2-1.8-8.4" /></>,
  languages: <><circle cx="12" cy="12" r="9" /><path d="M3 12h18" /><path d="M12 3c2.6 2.5 2.6 15.5 0 18M12 3c-2.6 2.5-2.6 15.5 0 18" /></>,
};

function CategoryIcon({ type, className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className={className}>
      {CATEGORY_ICON_PATHS[type]}
    </svg>
  );
}

// Circular progress gauge for overall profile completion. Colour shifts by tier
// (amber while getting started, purple mid-way, green once complete) and the stroke
// animates on change via a plain CSS transition on strokeDashoffset.
// Accepts a smaller default size on mobile via the `size` prop passed by the caller.
function CompletionRing({ percent, size = 108, stroke = 10 }) {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (Math.min(100, Math.max(0, percent)) / 100) * circumference;
  const color = percent === 100 ? '#10B981' : percent >= 60 ? '#9333EA' : '#F59E0B';

  return (
    <div className="relative shrink-0 mx-auto sm:mx-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#F3E8FF" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 0.8s cubic-bezier(0.4, 0, 0.2, 1), stroke 0.4s ease' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-xl sm:text-2xl font-extrabold text-[#1E1033] tabular-nums">{percent}%</span>
        <span className="text-[9px] font-bold tracking-wider text-gray-400 uppercase mt-0.5">Complete</span>
      </div>
    </div>
  );
}

function Profile() {
  const navigate = useNavigate();
  const [user] = useState(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [aadhaarNumber, setAadhaarNumber] = useState('');
  const [submittingAadhaar, setSubmittingAadhaar] = useState(false);

  // Admin-managed master data (skills, education, companies, etc.),
  // fetched once and merged into the static autocomplete lists below.
  const [masterData, setMasterData] = useState({});

    const [aiCitySuggestions, setAiCitySuggestions] = useState([]);
  const [aiCityLoading, setAiCityLoading] = useState(false);
  const cityDebounceRef = useRef(null);

 const MAX_PREFERRED_LOCATIONS = 10;
const MIN_PREFERRED_LOCATIONS = 5;
const MAX_SKILLS = 10;
const MIN_SKILLS = 5;

  const [preferredLocationInput, setPreferredLocationInput] = useState('');
  const [aiPreferredLocationSuggestions, setAiPreferredLocationSuggestions] = useState([]);
  const [aiPreferredLocationLoading, setAiPreferredLocationLoading] = useState(false);
  const preferredLocationDebounceRef = useRef(null);

  const [aiJobTitleSuggestions, setAiJobTitleSuggestions] = useState([]);
  const [aiJobTitleLoading, setAiJobTitleLoading] = useState(false);
  const jobTitleDebounceRef = useRef(null);

  const [aiSkillSuggestions, setAiSkillSuggestions] = useState([]);
  const [aiSkillLoading, setAiSkillLoading] = useState(false);
  const skillDebounceRef = useRef(null);

    const [aiCompanySuggestions, setAiCompanySuggestions] = useState([]);
  const [aiCompanyLoading, setAiCompanyLoading] = useState(false);
  const companyDebounceRef = useRef(null);

    const [aiInstitutionSuggestions, setAiInstitutionSuggestions] = useState([]);
  const [aiInstitutionLoading, setAiInstitutionLoading] = useState(false);
  const institutionDebounceRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '', phone: '', date_of_birth: '', gender: '',
    address: '', city: '', state: '', pincode: '', career_objective: '',expected_salary: '', expected_salary_period: 'year',
    preferred_locations: [],  preferred_job_designations: []
  });

  const [resumeFile, setResumeFile] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [uploadingResume, setUploadingResume] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [resumeParsedData, setResumeParsedData] = useState(null);

  const [skills, setSkills] = useState([]);
  const [educations, setEducations] = useState([]);
  const [experiences, setExperiences] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [languages, setLanguages] = useState([]);

  const [newSkill, setNewSkill] = useState({ skill_name: '', proficiency: 'intermediate' });
  const [newEducation, setNewEducation] = useState({ degree: '', institution: '', board_university: '', start_year: '', end_year: '', percentage_cgpa: '' });
  const [newExperience, setNewExperience] = useState({ job_title: '', company_name: '', start_date: '', end_date: '', currently_working: false, description: '' });
  const [newCertification, setNewCertification] = useState({ certificate_name: '', issued_by: '', issue_date: '', expiry_date: '', credential_url: '' });
  const [newLanguage, setNewLanguage] = useState({ language_name: '', proficiency: 'conversational' });

  // Featured Profile (Phase 8 - Premium Features)
  const [featured, setFeatured] = useState({ is_featured: false, featured_until: null, days_remaining: 0 });
  const [activatingFeatured, setActivatingFeatured] = useState(false);

  // Resume Boost (Phase 8 - Premium Features)
  const [resumeBoost, setResumeBoost] = useState({ is_boosted: false, boost_until: null, days_remaining: 0 });
  const [activatingBoost, setActivatingBoost] = useState(false);

const MAX_PREFERRED_DESIGNATIONS = 10;
const MIN_PREFERRED_DESIGNATIONS = 5;

const [activeTab, setActiveTab] = useState('personal');
const [designationInput, setDesignationInput] = useState('');
const [aiDesignationSuggestions, setAiDesignationSuggestions] = useState([]);
const [aiDesignationLoading, setAiDesignationLoading] = useState(false);
const designationDebounceRef = useRef(null);


  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    const fetchProfile = async () => {
      try {
        const response = await api.get('/profile');
        setProfile(response.data);
        setResumeParsedData(response.data.resume_parsed_data || null);

        setFormData({
          name: response.data.name || '',
          phone: response.data.phone || '',
          date_of_birth: response.data.date_of_birth || '',
          gender: response.data.gender || '',
          address: response.data.address || '',
          city: response.data.city || '',
          state: response.data.state || '',
          pincode: response.data.pincode || '',
          career_objective: response.data.career_objective || '',
          expected_salary: response.data.expected_salary || '',
          expected_salary_period: response.data.expected_salary_period || 'year',
          preferred_locations: response.data.preferred_locations || [],
        preferred_job_designations: response.data.preferred_job_designations || [], // 👈 add this
        });

      } catch (err) {
        console.error('Failed to fetch profile', err);
      } finally {
        setLoading(false);
      }
    };

    const fetchAllSections = async () => {
      try {
        const [skillsRes, eduRes, expRes, certRes, langRes] = await Promise.all([
  api.get('/skills'),
  api.get('/educations'),
  api.get('/experiences'),
  api.get('/certifications'),
  api.get('/languages'),
]);
setSkills(Array.isArray(skillsRes.data) ? skillsRes.data : skillsRes.data.data || []);
setEducations(Array.isArray(eduRes.data) ? eduRes.data : eduRes.data.data || []);
setExperiences(Array.isArray(expRes.data) ? expRes.data : expRes.data.data || []);
setCertifications(Array.isArray(certRes.data) ? certRes.data : certRes.data.data || []);
setLanguages(Array.isArray(langRes.data) ? langRes.data : langRes.data.data || []);
      } catch (err) {
        console.error('Failed to fetch profile sections', err);
      }
    };

    const fetchMasterData = async () => {
      try {
        const response = await api.get('/master-items');
        setMasterData(response.data || {});
      } catch (err) {
        console.error('Failed to fetch master data', err);
      }
    };

    const fetchFeaturedStatus = async () => {
      try {
        const response = await api.get('/profile/featured-status');
        setFeatured(response.data);
      } catch (err) {
        console.error('Failed to fetch featured status', err);
      }
    };

    const fetchResumeBoostStatus = async () => {
      try {
        const response = await api.get('/profile/resume-boost-status');
        setResumeBoost(response.data);
      } catch (err) {
        console.error('Failed to fetch resume boost status', err);
      }
    };

    fetchProfile();
    fetchAllSections();
    fetchMasterData();
    fetchFeaturedStatus();
    fetchResumeBoostStatus();
  }, [navigate]);

  // Merge admin-added master values into each static list, de-duplicated.
  // Falls back to the static list alone if masterData hasn't loaded yet.
  const mergedList = (type) => {
    const staticList = MASTER_TYPE_TO_STATIC_LIST[type] || [];
    const adminList = masterData[type] || [];
    return Array.from(new Set([...adminList, ...staticList]));
  };


    const fetchAiCitySuggestions = async (query) => {
    if (!query.trim() || query.trim().length < 2) { setAiCitySuggestions([]); return; }
    setAiCityLoading(true);
    try {
      const res = await api.post('/ai/suggest-cities', { query: query.trim() });
      // Backend returns "City, State" — strip the state so it matches the plain
      // city-name values the rest of this form (and the location filter) expect.
      setAiCitySuggestions((res.data.suggestions || []).map((s) => s.split(',')[0].trim()));
    } catch (err) {
      setAiCitySuggestions([]);
    } finally {
      setAiCityLoading(false);
    }
  };

  const handleCityChange = (text) => {
    setFormData((f) => ({ ...f, city: text }));
    if (cityDebounceRef.current) clearTimeout(cityDebounceRef.current);
    cityDebounceRef.current = setTimeout(() => fetchAiCitySuggestions(text), 350);
  };

  const selectCity = (text) => {
    setFormData((f) => ({ ...f, city: text }));
    setAiCitySuggestions([]);
  };

  const fetchAiPreferredLocationSuggestions = async (query) => {
    if (!query.trim() || query.trim().length < 2) { setAiPreferredLocationSuggestions([]); return; }
    setAiPreferredLocationLoading(true);
    try {
      const res = await api.post('/ai/suggest-cities', { query: query.trim() });
      setAiPreferredLocationSuggestions((res.data.suggestions || []).map((s) => s.split(',')[0].trim()));
    } catch (err) {
      setAiPreferredLocationSuggestions([]);
    } finally {
      setAiPreferredLocationLoading(false);
    }
  };

  const handlePreferredLocationChange = (text) => {
    setPreferredLocationInput(text);
    if (preferredLocationDebounceRef.current) clearTimeout(preferredLocationDebounceRef.current);
    preferredLocationDebounceRef.current = setTimeout(() => fetchAiPreferredLocationSuggestions(text), 350);
  };

  const addPreferredLocation = async (v) => {
    const val = v.trim();
    if (!val) return;
    if (formData.preferred_locations.includes(val) || formData.preferred_locations.length >= MAX_PREFERRED_LOCATIONS) return;

    const updated = [...formData.preferred_locations, val];
    setFormData((f) => ({ ...f, preferred_locations: updated }));
    setPreferredLocationInput('');
    setAiPreferredLocationSuggestions([]);

    try {
      await api.put('/profile', { preferred_locations: updated });
    } catch (err) {
      console.warn('Failed to save preferred location', err.response?.data || err.message);
      // Roll back on failure so the UI doesn't show a location that wasn't actually saved
      setFormData((f) => ({ ...f, preferred_locations: f.preferred_locations.filter((loc) => loc !== val) }));
    }
  };


  const fetchAiDesignationSuggestions = async (query) => {
  if (!query.trim() || query.trim().length < 2) { setAiDesignationSuggestions([]); return; }
  setAiDesignationLoading(true);
  try {
    const res = await api.post('/ai/suggest-keywords', { query: query.trim() });
    setAiDesignationSuggestions(res.data.suggestions || []);
  } catch (err) {
    setAiDesignationSuggestions([]);
  } finally {
    setAiDesignationLoading(false);
  }
};

const handleDesignationChange = (text) => {
  setDesignationInput(text);
  if (designationDebounceRef.current) clearTimeout(designationDebounceRef.current);
  designationDebounceRef.current = setTimeout(() => fetchAiDesignationSuggestions(text), 350);
};

const addPreferredDesignation = async (v) => {
  const val = v.trim();
  if (!val) return;
  if (formData.preferred_job_designations.includes(val) || formData.preferred_job_designations.length >= MAX_PREFERRED_DESIGNATIONS) return;

  const updated = [...formData.preferred_job_designations, val];
  setFormData((f) => ({ ...f, preferred_job_designations: updated }));
  setDesignationInput('');
  setAiDesignationSuggestions([]);

  try {
    await api.put('/profile', { preferred_job_designations: updated });
  } catch (err) {
    console.warn('Failed to save preferred designation', err.response?.data || err.message);
    setFormData((f) => ({ ...f, preferred_job_designations: f.preferred_job_designations.filter((d) => d !== val) }));
  }
};

const removePreferredDesignation = async (i) => {
  const removed = formData.preferred_job_designations[i];
  const updated = formData.preferred_job_designations.filter((_, idx) => idx !== i);
  setFormData((f) => ({ ...f, preferred_job_designations: updated }));

  try {
    await api.put('/profile', { preferred_job_designations: updated });
  } catch (err) {
    console.warn('Failed to remove preferred designation', err.response?.data || err.message);
    setFormData((f) => ({ ...f, preferred_job_designations: [...f.preferred_job_designations, removed] }));
  }
};

  const removePreferredLocation = async (i) => {
    const removed = formData.preferred_locations[i];
    const updated = formData.preferred_locations.filter((_, idx) => idx !== i);
    setFormData((f) => ({ ...f, preferred_locations: updated }));

    try {
      await api.put('/profile', { preferred_locations: updated });
    } catch (err) {
      console.warn('Failed to remove preferred location', err.response?.data || err.message);
      // Roll back on failure
      setFormData((f) => ({ ...f, preferred_locations: [...f.preferred_locations, removed] }));
    }
  };

  const fetchAiJobTitleSuggestions = async (query) => {
    if (!query.trim() || query.trim().length < 2) { setAiJobTitleSuggestions([]); return; }
    setAiJobTitleLoading(true);
    try {
      const res = await api.post('/ai/suggest-keywords', { query: query.trim() });
      setAiJobTitleSuggestions(res.data.suggestions || []);
    } catch (err) {
      setAiJobTitleSuggestions([]);
    } finally {
      setAiJobTitleLoading(false);
    }
  };

  const handleJobTitleChange = (text) => {
    setNewExperience((e) => ({ ...e, job_title: text }));
    if (jobTitleDebounceRef.current) clearTimeout(jobTitleDebounceRef.current);
    jobTitleDebounceRef.current = setTimeout(() => fetchAiJobTitleSuggestions(text), 350);
  };

  const selectJobTitle = (text) => {
    setNewExperience((e) => ({ ...e, job_title: text }));
    setAiJobTitleSuggestions([]);
  };

  const fetchAiSkillSuggestions = async (query) => {
    if (!query.trim() || query.trim().length < 2) { setAiSkillSuggestions([]); return; }
    setAiSkillLoading(true);
    try {
      const res = await api.post('/ai/suggest-keywords', { query: query.trim() });
      setAiSkillSuggestions(res.data.suggestions || []);
    } catch (err) {
      setAiSkillSuggestions([]);
    } finally {
      setAiSkillLoading(false);
    }
  };

  const handleSkillNameChange = (text) => {
    setNewSkill((s) => ({ ...s, skill_name: text }));
    if (skillDebounceRef.current) clearTimeout(skillDebounceRef.current);
    skillDebounceRef.current = setTimeout(() => fetchAiSkillSuggestions(text), 350);
  };

    const selectSkillName = (text) => {
    setNewSkill((s) => ({ ...s, skill_name: text }));
    setAiSkillSuggestions([]);
  };

  const fetchAiCompanySuggestions = async (query) => {
    if (!query.trim() || query.trim().length < 2) { setAiCompanySuggestions([]); return; }
    setAiCompanyLoading(true);
    try {
      const res = await api.post('/ai/suggest-companies', { query: query.trim() });
      setAiCompanySuggestions(res.data.suggestions || []);
    } catch (err) {
      setAiCompanySuggestions([]);
    } finally {
      setAiCompanyLoading(false);
    }
  };

  const handleCompanyNameChange = (text) => {
    setNewExperience((e) => ({ ...e, company_name: text }));
    if (companyDebounceRef.current) clearTimeout(companyDebounceRef.current);
    companyDebounceRef.current = setTimeout(() => fetchAiCompanySuggestions(text), 350);
  };

   const selectCompanyName = (text) => {
    setNewExperience((e) => ({ ...e, company_name: text }));
    setAiCompanySuggestions([]);
  };

  const fetchAiInstitutionSuggestions = async (query) => {
    if (!query.trim() || query.trim().length < 2) { setAiInstitutionSuggestions([]); return; }
    setAiInstitutionLoading(true);
    try {
      const res = await api.post('/ai/suggest-institutions', {
        query: query.trim(),
        education: newEducation.degree || undefined, // tailors results to the selected degree, when known
      });
      setAiInstitutionSuggestions(res.data.suggestions || []);
    } catch (err) {
      setAiInstitutionSuggestions([]);
    } finally {
      setAiInstitutionLoading(false);
    }
  };

  const handleInstitutionChange = (text) => {
    setNewEducation((e) => ({ ...e, institution: text }));
    if (institutionDebounceRef.current) clearTimeout(institutionDebounceRef.current);
    institutionDebounceRef.current = setTimeout(() => fetchAiInstitutionSuggestions(text), 350);
  };

  const selectInstitution = (text) => {
    setNewEducation((e) => ({ ...e, institution: text }));
    setAiInstitutionSuggestions([]);
  };

  useEffect(() => {
    return () => {
      if (cityDebounceRef.current) clearTimeout(cityDebounceRef.current);
      if (jobTitleDebounceRef.current) clearTimeout(jobTitleDebounceRef.current);
      if (skillDebounceRef.current) clearTimeout(skillDebounceRef.current);
      if (companyDebounceRef.current) clearTimeout(companyDebounceRef.current);
      if (institutionDebounceRef.current) clearTimeout(institutionDebounceRef.current);
      if (preferredLocationDebounceRef.current) clearTimeout(preferredLocationDebounceRef.current);
if (designationDebounceRef.current) clearTimeout(designationDebounceRef.current); // 👈 add this
    };
  }, []);

  // Profile completion — 8 categories weighted equally (rather than one flat field
  // count), so e.g. "Skills" carries the same weight as all 9 personal-detail fields
  // combined. Each category reports a 0..1 fraction plus filled/total for display.
  const profileCompletion = useMemo(() => {
    const personalFields = [
  formData.name, formData.phone, formData.date_of_birth, formData.gender,
  formData.address, formData.city, formData.state, formData.pincode,
];
    const personalFilled = personalFields.filter(Boolean).length;

      const resumeDone = !!profile?.resume_path;
    const photoDone = !!profile?.profile_photo_path;
    const documentFilled = (resumeDone ? 1 : 0) + (photoDone ? 1 : 0);

    // Weights add up to 100, so overall = sum(fraction * weight).
    const categories = [
      { key: 'personal', label: 'Personal Details', icon: 'personal', weight: 10, filled: personalFilled, total: personalFields.length },
      // Resume is worth 10 and Photo 5, so the fraction is computed by hand
      { key: 'documents', label: 'Resume & Photo', icon: 'documents', weight: 15, filled: documentFilled, total: 2,
        fraction: ((resumeDone ? 10 : 0) + (photoDone ? 5 : 0)) / 15 },
      { key: 'verification', label: 'Verification', icon: 'verification', weight: 5, filled: profile?.aadhaar_status === 'verified' ? 1 : 0, total: 1 },
      { key: 'locations', label: 'Preferred Locations', icon: 'locations', weight: 10, filled: Math.min(formData.preferred_locations.length, MIN_PREFERRED_LOCATIONS), total: MIN_PREFERRED_LOCATIONS },
      { key: 'skills', label: 'Skills', icon: 'skills', weight: 10, filled: Math.min(skills.length, MIN_SKILLS), total: MIN_SKILLS },
      { key: 'education', label: 'Education', icon: 'education', weight: 10, filled: educations.length > 0 ? 1 : 0, total: 1 },
      { key: 'experience', label: 'Experience', icon: 'experience', weight: 10, filled: experiences.length > 0 ? 1 : 0, total: 1 },
      { key: 'certifications', label: 'Certifications', icon: 'certifications', weight: 10, filled: certifications.length > 0 ? 1 : 0, total: 1 },
      { key: 'languages', label: 'Languages', icon: 'languages', weight: 10, filled: languages.length > 0 ? 1 : 0, total: 1 },
      { key: 'designations', label: 'Preferred Designation', icon: 'experience', weight: 10, filled: Math.min(formData.preferred_job_designations.length, MIN_PREFERRED_DESIGNATIONS), total: MIN_PREFERRED_DESIGNATIONS },
    ].map((c) => ({ ...c, fraction: c.fraction ?? c.filled / c.total }));

    const overall = Math.round(categories.reduce((sum, c) => sum + c.fraction * c.weight, 0));
    return { overall, categories };
  }, [formData, profile, skills, educations, experiences, certifications, languages]);

  const skillsOptions = useMemo(() => mergedList('skill'), [masterData]);
  const degreesOptions = useMemo(() => mergedList('education'), [masterData]);
  const collegesOptions = useMemo(() => mergedList('institution'), [masterData]);
  const boardsOptions = useMemo(() => mergedList('board_university'), [masterData]);
  const jobTitlesOptions = useMemo(() => mergedList('job_title'), [masterData]);
  const companiesOptions = useMemo(() => mergedList('company'), [masterData]);
  const certificationsOptions = useMemo(() => mergedList('certification'), [masterData]);
  const issuersOptions = useMemo(() => mergedList('issued_by'), [masterData]);
  const languagesOptions = useMemo(() => mergedList('language'), [masterData]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');
    try {
      const response = await api.put('/profile', formData);
      setProfile(response.data.user);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      setMessage('Profile updated successfully!');
    } catch (err) {
      setMessage(err.response?.data?.message || 'Failed to update profile.');
    } finally {
      setSaving(false);
    }
  };

const handleResumeUpload = async (file) => {
  if (!file) return;
  setResumeFile(file);
  setUploadingResume(true);
  setMessage('');
  const data = new FormData();
  data.append('resume', file);
  try {
    const response = await api.post('/profile/resume', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    setMessage('Resume uploaded successfully!');
    setProfile({ ...profile, resume_path: response.data.resume_url });
    setResumeParsedData(response.data.parsed_data || null);
  } catch (err) {
    setMessage(err.response?.data?.message || 'Failed to upload resume.');
  } finally {
    setUploadingResume(false);
  }
};

  const handlePhotoUpload = async (file) => {
    if (!file) return;
    setPhotoFile(file);
    setUploadingPhoto(true);
    setMessage('');
    const data = new FormData();
    data.append('photo', file);
    try {
      const response = await api.post('/profile/photo', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setMessage('Photo uploaded successfully!');
      setProfile({ ...profile, profile_photo_path: response.data.photo_url });
    } catch (err) {
      setMessage(err.response?.data?.message || 'Failed to upload photo.');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleAadhaarSubmit = async (e) => {
  e.preventDefault();
  if (!/^\d{12}$/.test(aadhaarNumber)) {
    setMessage('Aadhaar number must be exactly 12 digits.');
    return;
  }
  setSubmittingAadhaar(true);
  setMessage('');
  try {
    const response = await api.post('/profile/aadhaar', { aadhaar_number: aadhaarNumber });
    setProfile(response.data.user);
    setMessage('Aadhaar submitted for verification.');
    setAadhaarNumber('');
  } catch (err) {
    setMessage(err.response?.data?.message || 'Failed to submit Aadhaar.');
  } finally {
    setSubmittingAadhaar(false);
  }
};

  // Featured Profile — stub activation (no payment yet). Swap the endpoint's
  // internals for real Razorpay verification later; this call site stays the same.
  const handleActivateFeatured = async () => {
    setActivatingFeatured(true);
    setMessage('');
    try {
      const response = await api.post('/profile/featured-status/activate');
      setFeatured({
        is_featured: true,
        featured_until: response.data.featured_until,
        days_remaining: 30,
      });
      setMessage(response.data.message);
    } catch (err) {
      setMessage(err.response?.data?.message || 'Failed to activate Featured Profile.');
    } finally {
      setActivatingFeatured(false);
    }
  };

  // Resume Boost — stub activation (no payment yet), mirrors handleActivateFeatured.
  // Swap the endpoint's internals for real Razorpay verification later.
  const handleActivateResumeBoost = async () => {
    setActivatingBoost(true);
    setMessage('');
    try {
      const response = await api.post('/profile/resume-boost-status/activate');
      setResumeBoost({
        is_boosted: true,
        boost_until: response.data.boost_until,
        days_remaining: response.data.days_remaining ?? 15,
      });
      setMessage(response.data.message);
    } catch (err) {
      setMessage(err.response?.data?.message || 'Failed to activate Resume Boost.');
    } finally {
      setActivatingBoost(false);
    }
  };

  const addSkill = async (e) => {
  e.preventDefault();
  if (!newSkill.skill_name || skills.length >= MAX_SKILLS) return; // 👈 add limit check
  const response = await api.post('/skills', newSkill);
  setSkills([...skills, response.data.skill]);
  setNewSkill({ skill_name: '', proficiency: 'intermediate' });
};
  const deleteSkill = async (id) => {
    await api.delete(`/skills/${id}`);
    setSkills(skills.filter((s) => s.id !== id));
  };

  const addEducation = async (e) => {
    e.preventDefault();
    if (!newEducation.degree || !newEducation.institution || !newEducation.start_year) return;
    const response = await api.post('/educations', newEducation);
    setEducations([...educations, response.data.education]);
    setNewEducation({ degree: '', institution: '', board_university: '', start_year: '', end_year: '', percentage_cgpa: '' });
  };
  const deleteEducation = async (id) => {
    await api.delete(`/educations/${id}`);
    setEducations(educations.filter((e) => e.id !== id));
  };

  const addExperience = async (e) => {
    e.preventDefault();
    if (!newExperience.job_title || !newExperience.company_name || !newExperience.start_date) return;
    const response = await api.post('/experiences', newExperience);
    setExperiences([...experiences, response.data.experience]);
    setNewExperience({ job_title: '', company_name: '', start_date: '', end_date: '', currently_working: false, description: '' });
  };
  const deleteExperience = async (id) => {
    await api.delete(`/experiences/${id}`);
    setExperiences(experiences.filter((e) => e.id !== id));
  };

  const addCertification = async (e) => {
    e.preventDefault();
    if (!newCertification.certificate_name) return;
    const response = await api.post('/certifications', newCertification);
    setCertifications([...certifications, response.data.certification]);
    setNewCertification({ certificate_name: '', issued_by: '', issue_date: '', expiry_date: '', credential_url: '' });
  };
  const deleteCertification = async (id) => {
    await api.delete(`/certifications/${id}`);
    setCertifications(certifications.filter((c) => c.id !== id));
  };

  const addLanguage = async (e) => {
    e.preventDefault();
    if (!newLanguage.language_name) return;
    const response = await api.post('/languages', newLanguage);
    setLanguages([...languages, response.data.language]);
    setNewLanguage({ language_name: '', proficiency: 'conversational' });
  };
  const deleteLanguage = async (id) => {
    await api.delete(`/languages/${id}`);
    setLanguages(languages.filter((l) => l.id !== id));
  };

  if (loading) return <div className="p-10 text-center text-gray-500">Loading...</div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAF5FF] to-[#FDF2F8]">
      <Navbar user={user} />

      <div className="max-w-6xl mx-auto px-3 sm:px-4 py-6 sm:py-10 space-y-4 sm:space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#7C3AED] via-[#C026D3] to-[#EC4899] bg-clip-text text-transparent inline-block">My Profile</h2>
          <Link to="/resume-builder">
            <button className="bg-gradient-to-r from-[#7C3AED] to-[#DB2777] text-white px-6 py-2 rounded-lg font-medium hover:opacity-90 transition duration-300 shadow-md">
              Preview / Download Resume
            </button>
          </Link>
        </div>

        {/* Profile Strength */}
        <div className="bg-white rounded-2xl shadow border border-gray-100 p-4 sm:p-6">
          <div className="mb-5">
            <h3 className="text-base font-bold text-[#1E1033]">Profile Strength</h3>
            <p className="text-xs text-gray-400 mt-0.5">
              {profileCompletion.overall === 100
                ? 'All set — your profile is complete.'
                : profileCompletion.overall >= 75
                ? 'Almost there.'
                : profileCompletion.overall >= 40
                ? 'Good progress — keep going.'
                : 'Just getting started.'}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-6 sm:items-center">
            <CompletionRing percent={profileCompletion.overall} size={96} />

            <div className="flex-1 grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 gap-x-6 gap-y-3.5">
              {profileCompletion.categories.map((cat) => {
                const done = cat.fraction === 1;
                return (
                  <div key={cat.key} className="flex items-center gap-2.5">
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-colors ${
                        done ? 'bg-[#ECFDF5] text-[#10B981]' : 'bg-gray-50 text-gray-300'
                      }`}
                    >
                      <CategoryIcon type={cat.icon} className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className={`text-xs font-semibold truncate ${done ? 'text-[#1E1033]' : 'text-gray-400'}`}>
                          {cat.label}
                        </span>
                        {done ? (
                          <svg className="w-3.5 h-3.5 text-[#10B981] shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                            <path d="M4 12l5 5L20 6" />
                          </svg>
                        ) : (
                          cat.total > 1 && (
                            <span className="text-[10px] font-semibold text-gray-300 shrink-0 tabular-nums">
                              {cat.filled}/{cat.total}
                            </span>
                          )
                        )}
                      </div>
                      <div className="w-full h-1 bg-gray-100 rounded-full mt-1.5 overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-700 ${done ? 'bg-[#10B981]' : 'bg-[#9333EA]'}`}
                          style={{ width: `${cat.fraction * 100}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {profileCompletion.overall < 100 && (
            <div className="mt-5 pt-4 border-t border-gray-100 flex items-start gap-2 text-xs text-gray-500">
              <svg className="w-3.5 h-3.5 text-[#F59E0B] shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 9v4M12 17h.01M10.3 3.9L2.8 17a2 2 0 001.7 3h15a2 2 0 001.7-3L13.7 3.9a2 2 0 00-3.4 0z" />
              </svg>
              <span>
                Finish{' '}
                <strong className="text-[#1E1033] font-semibold">
                  {profileCompletion.categories
                    .filter((c) => c.fraction < 1)
                    .map((c) => c.label)
                    .join(', ')}
                </strong>{' '}
                to reach 100%.
              </span>
            </div>
          )}
        </div>

        {message && (
          <div className="bg-[#ECFDF5] text-[#10B981] p-3 rounded-lg text-sm font-medium">
            {message}
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-6 items-start">
          {/* Tab nav */}
          <div className="w-full lg:w-56 shrink-0 lg:sticky lg:top-6">
            <div className="bg-white rounded-xl shadow border border-gray-100 p-2 flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible">
              {[
                { key: 'personal', label: 'Personal Details' },
                { key: 'documents', label: 'Resume & Photo' },
                { key: 'aadhaar', label: 'Aadhaar' },
                { key: 'skills', label: 'Skills' },
                { key: 'locations', label: 'Preferred Locations' },
                { key: 'designations', label: 'Preferred Designation' },
                { key: 'education', label: 'Education' },
                { key: 'experience', label: 'Experience' },
                { key: 'certifications', label: 'Certifications' },
                { key: 'languages', label: 'Languages' },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`text-left px-3 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition ${
                    activeTab === tab.key
                      ? 'bg-gradient-to-r from-[#7C3AED] to-[#DB2777] text-white'
                      : 'text-gray-600 hover:bg-purple-50'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tab content */}
          <div className="flex-1 w-full space-y-6">

        {activeTab === 'personal' && (
        <form onSubmit={handleSubmit} className="bg-white p-4 sm:p-6 rounded-xl shadow space-y-4">
          <h3 className="font-bold text-[#1E1033] text-lg">Personal Details</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input type="text" name="name" placeholder="Full Name" value={formData.name} onChange={handleChange} className="border rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#9333EA] focus:border-transparent" />
            <input type="text" name="phone" placeholder="Phone Number" value={formData.phone} onChange={handleChange} className="border rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#9333EA] focus:border-transparent" />
            <input type="date" name="date_of_birth" value={formData.date_of_birth} onChange={handleChange} className="border rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#9333EA] focus:border-transparent" />
            <select name="gender" value={formData.gender} onChange={handleChange} className="border rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#9333EA] focus:border-transparent">
  <option value="">Select Gender</option>
  <option value="male">Male</option>
  <option value="female">Female</option>
  <option value="non-binary">Non-binary</option>
  <option value="prefer not to say">Prefer not to say</option>
  <option value="other">Other</option>
</select>
            <input type="text" name="address" placeholder="Address" value={formData.address} onChange={handleChange} className="border rounded-lg px-3 py-2 sm:col-span-2 w-full focus:outline-none focus:ring-2 focus:ring-[#9333EA] focus:border-transparent" />
                       <div>
              <Autocomplete
                options={[...aiCitySuggestions, ...CITIES_LIST.filter((c) => !aiCitySuggestions.includes(c))]}
                value={formData.city}
                onChange={handleCityChange}
                onSelect={selectCity}
                placeholder="City"
              />
              {aiCityLoading && <p className="text-xs text-gray-400 mt-1">Searching…</p>}
            </div>

            <Autocomplete
              options={STATES_LIST}
              value={formData.state}
              onChange={(text) => setFormData({ ...formData, state: text })}
              onSelect={(text) => setFormData({ ...formData, state: text })}
              placeholder="State"
            />
            <input type="text" name="pincode" placeholder="Pincode" value={formData.pincode} onChange={handleChange} className="border rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#9333EA] focus:border-transparent" />
          </div>
          <h3 className="font-bold text-[#1E1033] text-lg pt-2">Career Objective</h3>
         <textarea name="career_objective" value={formData.career_objective} onChange={handleChange} rows={3} placeholder="A short summary of your career goals" className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#9333EA] focus:border-transparent" />
          
         
          
          <div>
  <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Expected Salary (₹)</label>
  <div className="flex gap-2 mb-2 w-full sm:w-64">
    {['month', 'year'].map((p) => (
      <button
        key={p}
        type="button"
        onClick={() => setFormData((f) => ({ ...f, expected_salary_period: p }))}
        className={`flex-1 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
          formData.expected_salary_period === p
            ? 'bg-gradient-to-r from-[#7C3AED] to-[#DB2777] text-white border-transparent'
            : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'
        }`}
      >
        Per {p}
      </button>
    ))}
  </div>
  <input
    type="number"
    name="expected_salary"
    placeholder="e.g. 1200000"
    value={formData.expected_salary}
    onChange={handleChange}
    className="border rounded-lg px-3 py-2 w-full sm:w-64 focus:outline-none focus:ring-2 focus:ring-[#9333EA] focus:border-transparent"
  />
</div>
          <button type="submit" disabled={saving} className="w-full sm:w-auto bg-gradient-to-r from-[#7C3AED] to-[#DB2777] text-white px-6 py-2 rounded-lg font-semibold hover:opacity-90 disabled:opacity-50">
            {saving ? 'Saving...' : 'Save Profile'}
          </button>
        </form>
        )}

        {activeTab === 'documents' && (
        <div className="bg-white p-4 sm:p-6 rounded-xl shadow space-y-6">
          <div>
            <h3 className="font-bold text-[#1E1033] text-lg mb-2">Resume</h3>
            {profile?.resume_path && (
              <p className="text-sm text-gray-500 mb-2">
                Current resume:{' '}
                <button
                  type="button"
                  onClick={() =>
                    window.open(
                      profile.resume_path.startsWith('http')
                        ? profile.resume_path
                        : `${BACKEND_URL}/storage/${profile.resume_path}`,
                      '_blank'
                    )
                  }
                  className="text-[#9333EA] hover:underline bg-transparent border-none p-0 cursor-pointer font-medium"
                >
                  View
                </button>
              </p>
            )}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={(e) => handleResumeUpload(e.target.files[0])}
                disabled={uploadingResume}
                className="text-sm w-full sm:w-auto disabled:opacity-50"
              />
              {uploadingResume && <span className="text-sm text-gray-400">Uploading...</span>}
            </div>
          </div>

          <div>
  <h3 className="font-bold text-[#1E1033] text-lg mb-2">Profile Photo</h3>
  {profile?.profile_photo_path ? (
    <img
      src={profile.profile_photo_path.startsWith('http') ? profile.profile_photo_path : `${BACKEND_URL}/storage/${profile.profile_photo_path}`}
      alt="Profile"
      className="w-20 h-20 rounded-full object-cover mb-2"
    />
  ) : (
    <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mb-2 text-gray-400">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className="w-9 h-9">
        <circle cx="12" cy="8" r="3.4" />
        <path d="M5 20c0-3.6 3.1-6.2 7-6.2s7 2.6 7 6.2" />
      </svg>
    </div>
  )}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <input
                type="file"
                accept=".jpg,.jpeg,.png"
                onChange={(e) => handlePhotoUpload(e.target.files[0])}
                disabled={uploadingPhoto}
                className="text-sm w-full sm:w-auto disabled:opacity-50"
              />
              {uploadingPhoto && <span className="text-sm text-gray-400">Uploading...</span>}
            </div>
          </div>
        </div>
        )}

        {/* Resume Insights — AI-parsed data from the uploaded resume file (Section 7) */}
{activeTab === 'aadhaar' && (
<>
{profile?.resume_path && (
  <div className="bg-white p-4 sm:p-6 rounded-xl shadow">
    <h3 className="font-bold text-[#1E1033] text-lg mb-3">Resume Insights</h3>

    {!resumeParsedData ? (
      <p className="text-sm text-gray-400">
        No parsed data available. This can happen for .doc/.docx uploads — only PDF resumes are parsed automatically.
      </p>
    ) : (
      <div className="space-y-4">
        {resumeParsedData.skills?.length > 0 && (
          <div>
            <p className="text-xs font-bold text-gray-500 uppercase mb-2">Detected Skills</p>
            <div className="flex flex-wrap gap-2">
              {resumeParsedData.skills.map((skill, i) => (
                <span key={i} className="bg-purple-50 text-[#9333EA] text-sm font-medium px-3 py-1 rounded-full">
                  {skill}
                </span>
              ))}
            </div>
          </div>
        )}

        {resumeParsedData.experience?.length > 0 && (
          <div>
            <p className="text-xs font-bold text-gray-500 uppercase mb-2">Experience</p>
            <div className="space-y-2">
              {resumeParsedData.experience.map((exp, i) => (
                <div key={i} className="border rounded-lg p-3">
                  <p className="font-semibold text-[#1E1033] text-sm">{exp.job_title}</p>
                  <p className="text-sm text-gray-600">{exp.company}{exp.duration && ` · ${exp.duration}`}</p>
                  {exp.description && <p className="text-sm text-gray-500 mt-1">{exp.description}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {resumeParsedData.education?.length > 0 && (
          <div>
            <p className="text-xs font-bold text-gray-500 uppercase mb-2">Education</p>
            <div className="space-y-2">
              {resumeParsedData.education.map((edu, i) => (
                <div key={i} className="border rounded-lg p-3">
                  <p className="font-semibold text-[#1E1033] text-sm">{edu.degree}</p>
                  <p className="text-sm text-gray-600">{edu.institution}{edu.year_range && ` · ${edu.year_range}`}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {resumeParsedData.certifications?.length > 0 && (
          <div>
            <p className="text-xs font-bold text-gray-500 uppercase mb-2">Certifications</p>
            <div className="flex flex-wrap gap-2">
              {resumeParsedData.certifications.map((cert, i) => (
                <span key={i} className="bg-[#ECFDF5] text-[#10B981] text-sm font-medium px-3 py-1 rounded-full">
                  {cert}
                </span>
              ))}
            </div>
          </div>
        )}

        {resumeParsedData.languages?.length > 0 && (
          <div>
            <p className="text-xs font-bold text-gray-500 uppercase mb-2">Languages</p>
            <div className="flex flex-wrap gap-2">
              {resumeParsedData.languages.map((lang, i) => (
                <span key={i} className="bg-gray-100 text-gray-700 text-sm font-medium px-3 py-1 rounded-full">
                  {lang}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    )}
  </div>
)}


        {/* Aadhaar Verification */}
<div className="bg-white p-4 sm:p-6 rounded-xl shadow">
  <h3 className="font-bold text-[#1E1033] text-lg mb-3">Aadhaar Verification</h3>

  <div className="flex items-center gap-2 mb-4">
    <span className="text-sm text-gray-500">Status:</span>
    <span
      className={`text-xs font-semibold px-3 py-1 rounded-full capitalize ${
        profile?.aadhaar_status === 'verified'
          ? 'bg-[#ECFDF5] text-[#10B981]'
          : profile?.aadhaar_status === 'pending'
          ? 'bg-[#FFF7ED] text-[#F59E0B]'
          : profile?.aadhaar_status === 'rejected'
          ? 'bg-red-100 text-red-600'
          : 'bg-gray-100 text-gray-500'
      }`}
    >
      {profile?.aadhaar_status?.replace('_', ' ') || 'Not Submitted'}
    </span>
  </div>

  {(profile?.aadhaar_status === 'not_submitted' || profile?.aadhaar_status === 'rejected') && (
    <form onSubmit={handleAadhaarSubmit} className="flex flex-col sm:flex-row sm:items-center gap-3">
      <input
        type="text"
        maxLength={12}
        placeholder="12-digit Aadhaar number"
        value={aadhaarNumber}
        onChange={(e) => setAadhaarNumber(e.target.value.replace(/\D/g, ''))}
        className="border rounded-lg px-3 py-2 flex-1 w-full focus:outline-none focus:ring-2 focus:ring-[#9333EA] focus:border-transparent"
      />
      <button
        type="submit"
        disabled={submittingAadhaar}
        className="w-full sm:w-auto bg-gradient-to-r from-[#7C3AED] to-[#DB2777] text-white px-4 py-2 rounded-lg font-semibold text-sm hover:opacity-90 disabled:opacity-50"
      >
        {submittingAadhaar ? 'Submitting...' : 'Submit'}
      </button>
    </form>
  )}

  {profile?.aadhaar_status === 'pending' && (
    <p className="text-sm text-gray-500">
      Your Aadhaar is under review. This usually takes 1-2 business days.
    </p>
  )}

  {profile?.aadhaar_status === 'verified' && (
    <p className="text-sm text-gray-500">
      Your identity has been verified. ✅
    </p>
  )}
</div>
</>
)}

{activeTab === 'skills' && (
<div className="bg-white p-4 sm:p-6 rounded-xl shadow">
  <h3 className="font-bold text-[#1E1033] text-lg mb-3">Skills</h3>
  <div className="flex flex-wrap gap-2 mb-4">
    {skills.map((s) => (
      <span key={s.id} className="bg-purple-50 text-[#9333EA] text-sm font-medium px-3 py-1 rounded-full flex items-center gap-2">
        {s.skill_name} <span className="text-xs opacity-70 capitalize">({s.proficiency})</span>
        <button onClick={() => deleteSkill(s.id)} className="text-red-500 font-bold hover:text-red-700">×</button>
      </span>
    ))}
    {skills.length === 0 && <p className="text-sm text-gray-400">No skills added yet.</p>}
  </div>

  {skills.length < MAX_SKILLS ? (
    <form onSubmit={addSkill} className="flex flex-col sm:flex-row gap-2">
      <div className="flex-1">
        <Autocomplete
          options={[...aiSkillSuggestions, ...skillsOptions.filter((s) => !aiSkillSuggestions.includes(s))]}
          value={newSkill.skill_name}
          onChange={handleSkillNameChange}
          onSelect={selectSkillName}
          placeholder="Skill name (e.g. React)"
        />
        {aiSkillLoading && <p className="text-xs text-gray-400 mt-1">Searching…</p>}
      </div>

      <select value={newSkill.proficiency} onChange={(e) => setNewSkill({ ...newSkill, proficiency: e.target.value })} className="border rounded-lg px-3 py-2 w-full sm:w-auto focus:outline-none focus:ring-2 focus:ring-[#9333EA] focus:border-transparent">
        <option value="beginner">Beginner</option>
        <option value="intermediate">Intermediate</option>
        <option value="advanced">Advanced</option>
        <option value="expert">Expert</option>
      </select>
      <button type="submit" className="w-full sm:w-auto bg-gradient-to-r from-[#7C3AED] to-[#DB2777] text-white px-4 py-2 rounded-lg font-semibold hover:opacity-90">Add</button>
    </form>
  ) : (
    <p className="text-xs text-gray-400">Maximum of {MAX_SKILLS} skills reached. Remove one to add another.</p>
  )}
  <p className={`text-xs mt-2 ${skills.length < MIN_SKILLS ? 'text-amber-500 font-medium' : 'text-gray-400'}`}>
  {skills.length}/{MAX_SKILLS} skills added{skills.length < MIN_SKILLS && ` — add at least ${MIN_SKILLS}`}
</p>
</div>
)}

        {activeTab === 'locations' && (
        <div className="bg-white p-4 sm:p-6 rounded-xl shadow">
          <h3 className="font-bold text-[#1E1033] text-lg mb-3">Preferred Locations</h3>
          <div className="flex flex-wrap gap-2 mb-4">
            {formData.preferred_locations.map((loc, i) => (
              <span key={loc} className="bg-purple-50 text-[#9333EA] text-sm font-medium px-3 py-1 rounded-full flex items-center gap-2">
                {loc}
                <button onClick={() => removePreferredLocation(i)} className="text-red-500 font-bold hover:text-red-700">×</button>
              </span>
            ))}
            {formData.preferred_locations.length === 0 && <p className="text-sm text-gray-400">No preferred locations added yet.</p>}
          </div>
          {formData.preferred_locations.length < MAX_PREFERRED_LOCATIONS && (
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="flex-1">
                <Autocomplete
                  options={[...aiPreferredLocationSuggestions, ...CITIES_LIST.filter((c) => !aiPreferredLocationSuggestions.includes(c))]}
                  value={preferredLocationInput}
                  onChange={handlePreferredLocationChange}
                  onSelect={addPreferredLocation}
                  placeholder="Add a city (e.g. Bengaluru)"
                />
                {aiPreferredLocationLoading && <p className="text-xs text-gray-400 mt-1">Searching…</p>}
              </div>
              <button
                type="button"
                onClick={() => addPreferredLocation(preferredLocationInput)}
                disabled={!preferredLocationInput.trim()}
                className="w-full sm:w-auto bg-gradient-to-r from-[#7C3AED] to-[#DB2777] text-white px-4 py-2 rounded-lg font-semibold hover:opacity-90 disabled:opacity-50 h-fit"
              >
                Add
              </button>
            </div>
          )}
          <p className={`text-xs mt-2 ${formData.preferred_locations.length < MIN_PREFERRED_LOCATIONS ? 'text-amber-500 font-medium' : 'text-gray-400'}`}>
  {formData.preferred_locations.length}/{MAX_PREFERRED_LOCATIONS} locations selected{formData.preferred_locations.length < MIN_PREFERRED_LOCATIONS && ` — add at least ${MIN_PREFERRED_LOCATIONS}`}
</p>
        </div>
        )}

{activeTab === 'designations' && (
<div className="bg-white p-4 sm:p-6 rounded-xl shadow">
  <h3 className="font-bold text-[#1E1033] text-lg mb-3">Preferred Job Designation</h3>
  <div className="flex flex-wrap gap-2 mb-4">
    {formData.preferred_job_designations.map((d, i) => (
      <span key={d} className="bg-purple-50 text-[#9333EA] text-sm font-medium px-3 py-1 rounded-full flex items-center gap-2">
        {d}
        <button onClick={() => removePreferredDesignation(i)} className="text-red-500 font-bold hover:text-red-700">×</button>
      </span>
    ))}
    {formData.preferred_job_designations.length === 0 && <p className="text-sm text-gray-400">No preferred designations added yet.</p>}
  </div>
  {formData.preferred_job_designations.length < MAX_PREFERRED_DESIGNATIONS && (
    <div className="flex flex-col sm:flex-row gap-2">
      <div className="flex-1">
        <Autocomplete
          options={[...aiDesignationSuggestions, ...jobTitlesOptions.filter((t) => !aiDesignationSuggestions.includes(t))]}
          value={designationInput}
          onChange={handleDesignationChange}
          onSelect={addPreferredDesignation}
          placeholder="Add a designation (e.g. Software Engineer)"
        />
        {aiDesignationLoading && <p className="text-xs text-gray-400 mt-1">Searching…</p>}
      </div>
      <button
        type="button"
        onClick={() => addPreferredDesignation(designationInput)}
        disabled={!designationInput.trim()}
        className="w-full sm:w-auto bg-gradient-to-r from-[#7C3AED] to-[#DB2777] text-white px-4 py-2 rounded-lg font-semibold hover:opacity-90 disabled:opacity-50 h-fit"
      >
        Add
      </button>
    </div>
  )}
  <p className={`text-xs mt-2 ${formData.preferred_job_designations.length < MIN_PREFERRED_DESIGNATIONS ? 'text-amber-500 font-medium' : 'text-gray-400'}`}>
  {formData.preferred_job_designations.length}/{MAX_PREFERRED_DESIGNATIONS} designations selected{formData.preferred_job_designations.length < MIN_PREFERRED_DESIGNATIONS && ` — add at least ${MIN_PREFERRED_DESIGNATIONS}`}
</p>
</div>
)}

        {activeTab === 'education' && (
        <div className="bg-white p-4 sm:p-6 rounded-xl shadow">
          <h3 className="font-bold text-[#1E1033] text-lg mb-3">Education</h3>
          <div className="space-y-3 mb-4">
            {educations.map((edu) => (
              <div key={edu.id} className="border rounded-lg p-3 flex flex-col sm:flex-row justify-between items-start gap-2">
                <div>
                  <p className="font-semibold text-[#1E1033]">{edu.degree}</p>
                  <p className="text-sm text-gray-600">{edu.institution} {edu.board_university && `· ${edu.board_university}`}</p>
                  <p className="text-xs text-gray-400">{edu.start_year} - {edu.end_year || 'Present'} {edu.percentage_cgpa && `· ${edu.percentage_cgpa}%`}</p>
                </div>
                <button onClick={() => deleteEducation(edu.id)} className="text-red-500 text-xs font-semibold hover:text-red-700 self-end sm:self-start">Delete</button>
              </div>
            ))}
            {educations.length === 0 && <p className="text-sm text-gray-400">No education added yet.</p>}
          </div>
          <form onSubmit={addEducation} className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Autocomplete
              options={degreesOptions}
              value={newEducation.degree}
              onChange={(text) => setNewEducation({ ...newEducation, degree: text })}
              onSelect={(text) => setNewEducation({ ...newEducation, degree: text })}
              placeholder="Degree (e.g. B.Tech CSE)"
            />
                       <div>
              <Autocomplete
                options={[...aiInstitutionSuggestions, ...collegesOptions.filter((c) => !aiInstitutionSuggestions.includes(c))]}
                value={newEducation.institution}
                onChange={handleInstitutionChange}
                onSelect={selectInstitution}
                placeholder="Institution"
              />
              {aiInstitutionLoading && <p className="text-xs text-gray-400 mt-1">Searching…</p>}
            </div>
            <Autocomplete
              options={boardsOptions}
              value={newEducation.board_university}
              onChange={(text) => setNewEducation({ ...newEducation, board_university: text })}
              onSelect={(text) => setNewEducation({ ...newEducation, board_university: text })}
              placeholder="Board/University"
            />
            <input type="number" placeholder="Start Year" value={newEducation.start_year} onChange={(e) => setNewEducation({ ...newEducation, start_year: e.target.value })} className="border rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#9333EA] focus:border-transparent" />
            <input type="number" placeholder="End Year" value={newEducation.end_year} onChange={(e) => setNewEducation({ ...newEducation, end_year: e.target.value })} className="border rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#9333EA] focus:border-transparent" />
            <input type="number" step="0.01" placeholder="Percentage / CGPA" value={newEducation.percentage_cgpa} onChange={(e) => setNewEducation({ ...newEducation, percentage_cgpa: e.target.value })} className="border rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#9333EA] focus:border-transparent" />
            <button type="submit" className="bg-gradient-to-r from-[#7C3AED] to-[#DB2777] text-white px-4 py-2 rounded-lg font-semibold hover:opacity-90 sm:col-span-2">Add Education</button>
          </form>
        </div>
        )}

        {activeTab === 'experience' && (
        <div className="bg-white p-4 sm:p-6 rounded-xl shadow">
          <h3 className="font-bold text-[#1E1033] text-lg mb-3">Experience</h3>
          <div className="space-y-3 mb-4">
            {experiences.map((exp) => (
              <div key={exp.id} className="border rounded-lg p-3 flex flex-col sm:flex-row justify-between items-start gap-2">
                <div>
                  <p className="font-semibold text-[#1E1033]">{exp.job_title}</p>
                  <p className="text-sm text-gray-600">{exp.company_name}</p>
                  <p className="text-xs text-gray-400">{exp.start_date} - {exp.currently_working ? 'Present' : exp.end_date}</p>
                  {exp.description && <p className="text-sm text-gray-600 mt-1">{exp.description}</p>}
                </div>
                <button onClick={() => deleteExperience(exp.id)} className="text-red-500 text-xs font-semibold hover:text-red-700 self-end sm:self-start">Delete</button>
              </div>
            ))}
            {experiences.length === 0 && <p className="text-sm text-gray-400">No experience added yet.</p>}
          </div>
          <form onSubmit={addExperience} className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <div>
              <Autocomplete
                options={[...aiJobTitleSuggestions, ...jobTitlesOptions.filter((t) => !aiJobTitleSuggestions.includes(t))]}
                value={newExperience.job_title}
                onChange={handleJobTitleChange}
                onSelect={selectJobTitle}
                placeholder="Job Title"
              />
              {aiJobTitleLoading && <p className="text-xs text-gray-400 mt-1">Searching…</p>}
            </div>
                        <div>
              <Autocomplete
                options={[...aiCompanySuggestions, ...companiesOptions.filter((c) => !aiCompanySuggestions.includes(c))]}
                value={newExperience.company_name}
                onChange={handleCompanyNameChange}
                onSelect={selectCompanyName}
                placeholder="Company Name"
              />
              {aiCompanyLoading && <p className="text-xs text-gray-400 mt-1">Searching…</p>}
            </div>


            <input type="date" placeholder="Start Date" value={newExperience.start_date} onChange={(e) => setNewExperience({ ...newExperience, start_date: e.target.value })} className="border rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#9333EA] focus:border-transparent" />
            <input type="date" placeholder="End Date" value={newExperience.end_date} onChange={(e) => setNewExperience({ ...newExperience, end_date: e.target.value })} disabled={newExperience.currently_working} className="border rounded-lg px-3 py-2 disabled:bg-gray-100 w-full focus:outline-none focus:ring-2 focus:ring-[#9333EA] focus:border-transparent" />
            <label className="flex items-center gap-2 text-sm text-gray-600 sm:col-span-2">
              <input type="checkbox" checked={newExperience.currently_working} onChange={(e) => setNewExperience({ ...newExperience, currently_working: e.target.checked })} className="accent-[#9333EA]" />
              I currently work here
            </label>
            <textarea placeholder="Description" value={newExperience.description} onChange={(e) => setNewExperience({ ...newExperience, description: e.target.value })} className="border rounded-lg px-3 py-2 sm:col-span-2 focus:outline-none focus:ring-2 focus:ring-[#9333EA] focus:border-transparent" rows={2} />
            <button type="submit" className="bg-gradient-to-r from-[#7C3AED] to-[#DB2777] text-white px-4 py-2 rounded-lg font-semibold hover:opacity-90 sm:col-span-2">Add Experience</button>
          </form>
        </div>
        )}

        {activeTab === 'certifications' && (
        <div className="bg-white p-4 sm:p-6 rounded-xl shadow">
          <h3 className="font-bold text-[#1E1033] text-lg mb-3">Certifications</h3>
          <div className="space-y-3 mb-4">
            {certifications.map((cert) => (
              <div key={cert.id} className="border rounded-lg p-3 flex flex-col sm:flex-row justify-between items-start gap-2">
                <div>
                  <p className="font-semibold text-[#1E1033]">{cert.certificate_name}</p>
                  <p className="text-sm text-gray-600">{cert.issued_by}</p>
                  <p className="text-xs text-gray-400">{cert.issue_date}</p>
                   <p className="text-xs text-gray-400">{cert.expiry_date}</p>
                </div>
                <button onClick={() => deleteCertification(cert.id)} className="text-red-500 text-xs font-semibold hover:text-red-700 self-end sm:self-start">Delete</button>
              </div>
            ))}
            {certifications.length === 0 && <p className="text-sm text-gray-400">No certifications added yet.</p>}
          </div>
          <form onSubmit={addCertification} className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Autocomplete
              options={certificationsOptions}
              value={newCertification.certificate_name}
              onChange={(text) => setNewCertification({ ...newCertification, certificate_name: text })}
              onSelect={(text) => setNewCertification({ ...newCertification, certificate_name: text })}
              placeholder="Certificate Name"
            />
            <Autocomplete
              options={issuersOptions}
              value={newCertification.issued_by}
              onChange={(text) => setNewCertification({ ...newCertification, issued_by: text })}
              onSelect={(text) => setNewCertification({ ...newCertification, issued_by: text })}
              placeholder="Issued By"
            />
            <input type="date" placeholder="Issue Date" value={newCertification.issue_date} onChange={(e) => setNewCertification({ ...newCertification, issue_date: e.target.value })} className="border rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#9333EA] focus:border-transparent" />
            <input type="date" placeholder="Expiry Date" value={newCertification.expiry_date} onChange={(e) => setNewCertification({ ...newCertification, expiry_date: e.target.value })} className="border rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#9333EA] focus:border-transparent" />
            <input type="text" placeholder="Credential URL" value={newCertification.credential_url} onChange={(e) => setNewCertification({ ...newCertification, credential_url: e.target.value })} className="border rounded-lg px-3 py-2 sm:col-span-2 w-full focus:outline-none focus:ring-2 focus:ring-[#9333EA] focus:border-transparent" />
            <button type="submit" className="bg-gradient-to-r from-[#7C3AED] to-[#DB2777] text-white px-4 py-2 rounded-lg font-semibold hover:opacity-90 sm:col-span-2">Add Certification</button>
          </form>
        </div>
        )}

        {activeTab === 'languages' && (
        <div className="bg-white p-4 sm:p-6 rounded-xl shadow">
          <h3 className="font-bold text-[#1E1033] text-lg mb-3">Languages</h3>
          <div className="flex flex-wrap gap-2 mb-4">
            {languages.map((lang) => (
              <span key={lang.id} className="bg-[#ECFDF5] text-[#10B981] text-sm font-medium px-3 py-1 rounded-full flex items-center gap-2">
                {lang.language_name} <span className="text-xs opacity-70 capitalize">({lang.proficiency})</span>
                <button onClick={() => deleteLanguage(lang.id)} className="text-red-500 font-bold hover:text-red-700">×</button>
              </span>
            ))}
            {languages.length === 0 && <p className="text-sm text-gray-400">No languages added yet.</p>}
          </div>
          <form onSubmit={addLanguage} className="flex flex-col sm:flex-row gap-2">
            <div className="flex-1">
              <Autocomplete
                options={languagesOptions}
                value={newLanguage.language_name}
                onChange={(text) => setNewLanguage({ ...newLanguage, language_name: text })}
                onSelect={(text) => setNewLanguage({ ...newLanguage, language_name: text })}
                placeholder="Language (e.g. English)"
              />
            </div>
            <select value={newLanguage.proficiency} onChange={(e) => setNewLanguage({ ...newLanguage, proficiency: e.target.value })} className="border rounded-lg px-3 py-2 w-full sm:w-auto focus:outline-none focus:ring-2 focus:ring-[#9333EA] focus:border-transparent">
              <option value="basic">Basic</option>
              <option value="conversational">Conversational</option>
              <option value="fluent">Fluent</option>
              <option value="native">Native</option>
            </select>
            <button type="submit" className="w-full sm:w-auto bg-gradient-to-r from-[#7C3AED] to-[#DB2777] text-white px-4 py-2 rounded-lg font-semibold hover:opacity-90">Add</button>
          </form>
        </div>
        )}

          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-3 sm:px-4 pb-8 text-center">
        <Link to="/account/delete" className="text-xs text-gray-400 hover:text-red-500 underline">
          Delete my account
        </Link>
      </div>
      <Footer />
    </div>
  );
}

export default Profile;